// Sin Offering: what was charged against whom, what they said about it, and what
// has actually moved.
//
// THE SHAPE OF THIS SCREEN IS AN ARGUMENT, so it is worth stating before the code.
//
// Nothing on this tab has been deducted from anybody, and on the day it was built
// nothing lawfully could be. A fine is not a debt, so BCEA section 34 does not
// permit taking it off a salary even with written consent, and for the three
// people who invoice it needs a clause in their agreement. The gate that stops it
// is in sin_offering_release_v1, in the database, because payroll_standing_items
// is pushed to SimplePay by its own job and Retool is still live for rollback: a
// gate in React would be a gate on one of three doors.
//
// So the page leads with that, says it again on every row that could be misread,
// and never lets an APPROVED charge look like money gone. `settlementSentence` in
// sinModel.ts holds that decision once so no component can quietly undo it, and
// web/test/sinModel.test.ts asserts that no status short of settled ever claims a
// deduction happened.
//
// THE OTHER THING THIS PAGE HAS TO SHOW HONESTLY is who set the date somebody is
// being charged for. On 1 September 2026, 22 of the 28 overdue task and assignee
// pairs on the board carried a target date written by monday-import, meeting-notes
// or system:vehicle-expiry, and 14 had never been touched by a human at all. The
// Proposed tab therefore prints target_set_by beside every candidate, and the
// clean slate in the policy keeps all 28 out of the ledger. A charge for a date a
// data migration chose is not one anybody can defend.
//
// Two columns exist purely so a defect cannot hide: "Answered", which is the BCEA
// section 34(2)(b) opportunity to make representations, and "Nobody to charge",
// which on that same day covered 13 unassigned undated tasks. A rule that
// silently drops what it cannot handle is worse than one that refuses out loud.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Scale,
  ListChecks,
  CalendarClock,
  Settings2,
  AlertCircle,
  ShieldAlert,
  Play,
} from "lucide-react";
import {
  useGetSinBoard,
  useDecideSinOffering,
  useMarkSinOfferingPaid,
  useRecordSinRepresentation,
  useAccrueSinOfferings,
  useReleaseSinOfferings,
} from "../hooks/backend/sin";
import { useDecideTargetDateChange } from "../hooks/backend/tasks";
import { Spinner } from "../lib/shadcn/spinner";
import { Button } from "../lib/shadcn/button";
import { Textarea } from "../lib/shadcn/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../lib/shadcn/tabs";
import { toast } from "../lib/shadcn/sonner";
import { Chip } from "../components/interviews/Chip";
import { writeErrorMessage } from "../utils/writeError";
import {
  RaiseSinDialog,
  type SinPerson,
} from "../components/sin/RaiseSinDialog";
import {
  zar,
  dateLabel,
  capLabel,
  statusTone,
  settlementSentence,
  awaitingAnswer,
  originLabel,
  releaseBanner,
  ruleSentences,
  type SinPolicy,
  type SinRow,
} from "../components/sin/sinModel";

type Row = Record<string, any>;

type Board = {
  ledger: SinRow[];
  positions: Row[];
  candidates: Row[];
  date_requests: Row[];
  policy: SinPolicy | null;
  people: Row[];
  can_approve: boolean;
  can_approve_dates: boolean;
  person_id: string | null;
};

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
      {hint && (
        <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}

export default function SinOffering() {
  const board = useGetSinBoard<Board>();
  const decide = useDecideSinOffering();
  const markPaid = useMarkSinOfferingPaid();
  const represent = useRecordSinRepresentation();
  const accrue = useAccrueSinOfferings<{ dry_run: boolean; rows: Row[] }>();
  const release = useReleaseSinOfferings();
  const decideDate = useDecideTargetDateChange();

  const [raiseOpen, setRaiseOpen] = useState(false);
  const [answerFor, setAnswerFor] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState("");
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [dryRows, setDryRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    void board.trigger({}, { skipCache: true });
  }, [board]);

  useEffect(() => {
    void board.trigger({}, { skipCache: true });
    // Mount only. The board is reloaded explicitly after every write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = board.data;
  const banner = useMemo(
    () => releaseBanner(data?.policy ?? null),
    [data?.policy],
  );

  // The people a charge can be raised against. The server sends the roster:
  // everybody with a cockpit login that is still live, with the settlement
  // route the database would use.
  //
  // This used to be derived here from the candidate and position rows, which
  // made the picker a by-product of what the rules had already proposed rather
  // than a list of people. Anybody no rule had named and no charge had touched
  // could not be selected at all, so the manual route could not reach them.
  //
  // Candidates and positions are still folded in behind the roster so that a
  // person the rules have named is never missing from the picker even if their
  // login is gone, and so an older backend that sends no roster still leaves
  // this screen working rather than empty.
  const people = useMemo<SinPerson[]>(() => {
    const seen = new Map<string, SinPerson>();
    for (const r of data?.people ?? []) {
      if (r.person_id && !seen.has(r.person_id)) {
        seen.set(r.person_id, {
          person_id: r.person_id,
          name: r.name ?? "Unknown",
          route: r.route ?? null,
        });
      }
    }
    for (const c of data?.candidates ?? []) {
      if (c.person_id && !seen.has(c.person_id)) {
        seen.set(c.person_id, {
          person_id: c.person_id,
          name: c.subject_name ?? "Unknown",
          route: c.settlement_route ?? null,
        });
      }
    }
    for (const p of data?.positions ?? []) {
      if (p.person_id && !seen.has(p.person_id)) {
        seen.set(p.person_id, {
          person_id: p.person_id,
          name: p.subject_name ?? "Unknown",
          route: p.settlement_route ?? null,
        });
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const pending = (data?.ledger ?? []).filter(
    (r) => r.status === "Pending approval",
  );
  const unanswered = (data?.ledger ?? []).filter((r) => awaitingAnswer(r));
  const openCandidates = (data?.candidates ?? []).filter(
    (c) => !c.blocked_reason,
  );
  const blockedCandidates = (data?.candidates ?? []).filter(
    (c) => c.blocked_reason,
  );
  const pendingDates = (data?.date_requests ?? []).filter(
    (r) => r.status === "Pending",
  );

  async function runAccrue(dry: boolean) {
    setBusy(true);
    try {
      const res = await accrue.trigger({ dry_run: dry }, { skipCache: true });
      const rows = (res as { rows?: Row[] } | null)?.rows ?? [];
      setDryRows(rows);
      if (dry) {
        const would = rows.filter((r) => r.action === "would raise").length;
        toast.success(
          `Dry run: ${would} would be raised, ${rows.length - would} skipped. Nothing was written.`,
        );
      } else {
        const raised = rows.filter((r) => r.action === "raised").length;
        toast.success(`${raised} raised, all waiting on a decision.`);
        load();
      }
    } catch (err) {
      toast.error(writeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitDecision(
    id: string,
    decision: string,
    note?: string | null,
  ) {
    setBusy(true);
    try {
      await decide.trigger(
        { id, decision, note: note ?? null },
        { skipCache: true },
      );
      setNoteFor(null);
      setNoteText("");
      toast.success(`Recorded as ${decision.toLowerCase()}.`);
      load();
    } catch (err) {
      // The database's own sentence reaches the operator unaltered, which is
      // where the "say why the answer does not change it" refusal comes from.
      toast.error(writeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitPaid(id: string) {
    setBusy(true);
    try {
      await markPaid.trigger({ id, note: null }, { skipCache: true });
      toast.success("Recorded as paid outside payroll.");
      load();
    } catch (err) {
      toast.error(writeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitAnswer(id: string) {
    if (!answerText.trim()) {
      toast.error("Nothing was written, so nothing was recorded.");
      return;
    }
    setBusy(true);
    try {
      await represent.trigger(
        { id, text: answerText.trim() },
        { skipCache: true },
      );
      setAnswerFor(null);
      setAnswerText("");
      toast.success("Your answer is on the record.");
      load();
    } catch (err) {
      toast.error(writeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function attemptRelease() {
    setBusy(true);
    try {
      const period = data?.ledger?.[0]?.period_start;
      if (!period) {
        toast.error("There is nothing on the ledger to release.");
        return;
      }
      const res = await release.trigger(
        { period_start: period },
        { skipCache: true },
      );
      const n =
        (res as { released_count?: number } | null)?.released_count ?? 0;
      toast.success(`${n} released.`);
      load();
    } catch (err) {
      // EXPECTED. sin_offering_release_v1 raises the reason and it is shown as
      // written, at length, because the reason is the answer.
      toast.error(writeErrorMessage(err), { duration: 20000 });
    } finally {
      setBusy(false);
    }
  }

  if (board.loading && !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (board.error && !data) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">
              The Sin Offering tab could not be loaded.
            </div>
            <div className="mt-1">{board.error}</div>
            <Button variant="outline" size="sm" className="mt-2" onClick={load}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Scale className="h-5 w-5" />
            Sin Offering
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Driving Change. What was charged, what was said about it, and what
            has moved.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setRaiseOpen(true)}>
            Raise one
          </Button>
          {data?.can_approve && (
            <>
              <Button
                variant="outline"
                onClick={() => runAccrue(true)}
                disabled={busy}
              >
                {busy && <Spinner className="mr-2 h-4 w-4" />}
                Dry run the rules
              </Button>
              <Button
                variant="outline"
                onClick={() => runAccrue(false)}
                disabled={busy}
              >
                <Play className="mr-2 h-4 w-4" />
                Run them for real
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Shown ONLY when something is off: no policy at all, or a live money
          path. The standing BCEA paragraph that used to sit here was removed on
          1 September 2026 at Luyanda's instruction. Nothing was lost that a
          person needs: the Actually deducted card reads R0.00 with "Nothing has
          moved", every row says the same through settlementSentence, and the
          full reason a deduction is not lawful yet is in
          sin_offering_release_v1, which says it at the moment somebody presses
          Settle. releaseBanner returns null the rest of the time. */}
      {banner && (
        <div
          className={
            "mb-4 flex items-start gap-2 rounded-lg border p-3 text-sm " +
            (banner.tone === "red"
              ? "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200"
              : banner.tone === "amber"
                ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
                : "border-border bg-muted/40 text-foreground")
          }
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{banner.text}</span>
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="On the ledger" value={String(data?.ledger?.length ?? 0)} />
        <Stat label="Waiting on a decision" value={String(pending.length)} />
        <Stat
          label="No written answer yet"
          value={String(unanswered.length)}
          hint="A charge is answered before anything could settle"
        />
        <Stat
          label="Actually deducted"
          value="R0.00"
          hint="Nothing has moved"
        />
      </div>

      <Tabs defaultValue="ledger" className="flex min-h-0 flex-1 flex-col">
        <TabsList>
          <TabsTrigger value="ledger">
            <ListChecks className="mr-1.5 h-4 w-4" />
            Ledger
          </TabsTrigger>
          <TabsTrigger value="proposed">
            Proposed
            {openCandidates.length > 0 && (
              <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs">
                {openCandidates.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="dates">
            <CalendarClock className="mr-1.5 h-4 w-4" />
            Date changes
            {pendingDates.length > 0 && (
              <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs">
                {pendingDates.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="policy">
            <Settings2 className="mr-1.5 h-4 w-4" />
            Policy
          </TabsTrigger>
        </TabsList>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="ledger" className="min-h-0 flex-1 overflow-auto">
          {(data?.ledger?.length ?? 0) === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              Nothing has been charged against anybody. The clean slate starts
              on{" "}
              {data?.policy
                ? dateLabel(data.policy.counts_from)
                : "the policy date"}
              , so nothing that was already late before then counts.
            </p>
          ) : (
            <div className="space-y-2 py-2">
              {(data?.ledger ?? []).map((r) => {
                const mine = data?.person_id && r.person_id === data.person_id;
                return (
                  <div
                    key={r.id}
                    className="rounded-lg border border-border bg-card p-3"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {r.subject_name ?? "Unknown"}
                      </span>
                      <span className="tabular-nums">{zar(r.amount_zar)}</span>
                      <Chip tone={statusTone(r.status)}>{r.status}</Chip>
                      {/* A ledger-only charge never touches a payslip or an
                          invoice, so naming either would say the opposite. */}
                      <Chip tone="neutral">
                        {r.ledger_only
                          ? "Ledger only"
                          : r.settlement_route === "payslip"
                            ? "Payslip"
                            : "Invoice"}
                      </Chip>
                      {awaitingAnswer(r) && (
                        <Chip tone="amber">No written answer</Chip>
                      )}
                    </div>

                    <div className="mt-1 text-sm text-muted-foreground">
                      {r.reason_label}
                      {r.task_ref
                        ? ` on task #${r.task_ref}, ${r.task_title ?? ""}`
                        : ""}
                      {r.incident_id
                        ? ` on the ${r.incident_registration ?? "unregistered vehicle"} incident, due ${dateLabel(r.missed_date)}`
                        : ""}
                      {". "}
                      {originLabel(r.origin, r.raised_by_name)}.
                    </div>
                    {r.reason && <div className="mt-1 text-sm">{r.reason}</div>}

                    {/* Never omitted, on any row. An approved charge beside a
                        rand figure reads as money gone unless it says otherwise. */}
                    <div className="mt-1.5 text-xs font-medium">
                      {settlementSentence(r)}
                    </div>

                    {r.representation && (
                      <div className="mt-2 border-l-2 border-border pl-3 text-sm">
                        <div className="text-xs text-muted-foreground">
                          What they said
                        </div>
                        {r.representation}
                      </div>
                    )}
                    {r.decision_note && (
                      <div className="mt-2 border-l-2 border-border pl-3 text-sm">
                        <div className="text-xs text-muted-foreground">
                          The decision
                        </div>
                        {r.decision_note}
                      </div>
                    )}

                    <div className="mt-2 flex flex-wrap gap-2">
                      {/* Anybody may answer their own charge. That is the point
                          of the column, and it is checked again in the database. */}
                      {mine && !r.representation && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAnswerFor(r.id)}
                        >
                          Answer this
                        </Button>
                      )}
                      {data?.can_approve && r.status === "Pending approval" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setNoteFor(r.id)}
                            disabled={busy}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => submitDecision(r.id, "Declined")}
                            disabled={busy}
                          >
                            Decline
                          </Button>
                        </>
                      )}
                      {/* Ledger only: an approved incident R50 settles by being
                          paid outside payroll, and nothing else ever settles it. */}
                      {data?.can_approve &&
                        r.status === "Approved" &&
                        r.ledger_only &&
                        !r.paid_outside_payroll && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => submitPaid(r.id)}
                            disabled={busy}
                          >
                            Mark paid
                          </Button>
                        )}
                      {data?.can_approve &&
                        r.status === "Approved" &&
                        !r.settled &&
                        !r.paid_outside_payroll && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => submitDecision(r.id, "Waived")}
                            disabled={busy}
                          >
                            Waive it
                          </Button>
                        )}
                      {data?.can_approve && !r.representation && !mine && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setAnswerFor(r.id)}
                        >
                          Record their answer
                        </Button>
                      )}
                    </div>

                    {answerFor === r.id && (
                      <div className="mt-2 space-y-2">
                        <Textarea
                          rows={3}
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          placeholder="In their words, or yours if you are writing it down for them."
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => submitAnswer(r.id)}
                            disabled={busy}
                          >
                            Record it
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setAnswerFor(null);
                              setAnswerText("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {noteFor === r.id && (
                      <div className="mt-2 space-y-2">
                        <Textarea
                          rows={3}
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder={
                            r.representation
                              ? "They have answered. Say why the answer does not change it."
                              : "Optional note."
                          }
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() =>
                              submitDecision(r.id, "Approved", noteText)
                            }
                            disabled={busy}
                          >
                            Approve it
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setNoteFor(null);
                              setNoteText("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {(data?.positions?.length ?? 0) > 0 && (
            <div className="mt-4 rounded-lg border border-border">
              <div className="border-b border-border px-3 py-2 text-sm font-medium">
                Per person, this payroll period
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Person</th>
                      <th className="px-3 py-2">Approved</th>
                      <th className="px-3 py-2">Waiting</th>
                      <th className="px-3 py-2">Quarter cap</th>
                      <th className="px-3 py-2">No answer yet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.positions ?? []).map((p) => (
                      <tr
                        key={`${p.person_id}-${p.period_start}`}
                        className="border-t border-border"
                      >
                        <td className="px-3 py-2">{p.subject_name}</td>
                        <td className="px-3 py-2 tabular-nums">
                          {zar(p.approved_zar)}
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {zar(p.pending_zar)}
                        </td>
                        {/* Not known, never R0.00. Remuneration is not on file
                            for anybody, and zero would read as "no deduction is
                            permitted" rather than "nobody has told us". */}
                        <td className="px-3 py-2 tabular-nums">
                          {capLabel(p.bcea_cap_zar)}
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {p.approved_without_representation ?? 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data?.can_approve && (
                <div className="border-t border-border px-3 py-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={attemptRelease}
                    disabled={busy}
                  >
                    Settle this period
                  </Button>
                  <p className="mt-1 text-xs text-muted-foreground">
                    This is expected to refuse. The reason it gives is the
                    answer.
                  </p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="proposed" className="min-h-0 flex-1 overflow-auto">
          <p className="py-2 text-sm text-muted-foreground">
            What the automatic rules would raise right now. Nothing here has
            been charged. Dry run first: the rules read dates, and some of those
            dates were written by an import or a scheduled job rather than by a
            person.
          </p>

          {openCandidates.length === 0 && blockedCandidates.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground">
              Neither rule has anything to propose.
            </p>
          )}

          {openCandidates.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Task</th>
                    <th className="px-3 py-2">Person</th>
                    <th className="px-3 py-2">Rule</th>
                    <th className="px-3 py-2">Date</th>
                    {/* The column that makes 22 of 28 visible. */}
                    <th className="px-3 py-2">Date set by</th>
                    <th className="px-3 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {openCandidates.map((c, i) => (
                    <tr
                      key={`${c.task_id}-${c.reason_code}-${i}`}
                      className="border-t border-border"
                    >
                      <td className="px-3 py-2">
                        #{c.ref} {c.title}
                      </td>
                      <td className="px-3 py-2">{c.subject_name}</td>
                      <td className="px-3 py-2">
                        {c.reason_code === "task_overdue"
                          ? "Past its target date"
                          : "No dates set"}
                      </td>
                      <td className="px-3 py-2">{c.event_date}</td>
                      <td className="px-3 py-2">
                        {String(c.target_set_by ?? "").includes("@") ? (
                          c.target_set_by
                        ) : (
                          <Chip tone="amber">
                            {c.target_set_by ?? "unknown"}
                          </Chip>
                        )}
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {zar(c.amount_zar)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {blockedCandidates.length > 0 && (
            <div className="mt-4 rounded-lg border border-border">
              <div className="border-b border-border px-3 py-2 text-sm font-medium">
                Would not be raised, and why
              </div>
              <ul className="divide-y divide-border">
                {blockedCandidates.map((c, i) => (
                  <li
                    key={`${c.task_id}-${c.reason_code}-b${i}`}
                    className="px-3 py-2 text-sm"
                  >
                    <span className="font-medium">
                      #{c.ref} {c.title}
                    </span>
                    <div className="text-muted-foreground">
                      {c.blocked_reason}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dryRows && (
            <div className="mt-4 rounded-lg border border-border">
              <div className="border-b border-border px-3 py-2 text-sm font-medium">
                Last run
              </div>
              <ul className="divide-y divide-border">
                {dryRows.map((r, i) => (
                  <li key={i} className="px-3 py-2 text-sm">
                    <Chip tone={r.action === "skipped" ? "neutral" : "amber"}>
                      {r.action}
                    </Chip>{" "}
                    #{r.task_ref} {r.task_title}, {r.subject_name}
                    <div className="text-muted-foreground">{r.note}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="dates" className="min-h-0 flex-1 overflow-auto">
          <p className="py-2 text-sm text-muted-foreground">
            A target date is set once. Changing one that is already set needs a
            reason and an approval, and clearing a date counts as changing it.
          </p>
          {(data?.date_requests?.length ?? 0) === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              No date changes have been asked for.
            </p>
          ) : (
            <div className="space-y-2 py-2">
              {(data?.date_requests ?? []).map((r) => (
                <div
                  key={r.id}
                  className="rounded-lg border border-border bg-card p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">
                      #{r.task_ref} {r.task_title}
                    </span>
                    <Chip
                      tone={
                        r.status === "Pending"
                          ? "amber"
                          : r.status === "Approved"
                            ? "green"
                            : "neutral"
                      }
                    >
                      {r.status}
                    </Chip>
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {r.requested_by_name} asked to move it from{" "}
                    {r.from_date ?? "no date"} to {r.to_date ?? "no date"}.
                  </div>
                  <div className="mt-1 text-sm">{r.reason}</div>
                  {r.decision_note && (
                    <div className="mt-2 border-l-2 border-border pl-3 text-sm">
                      {r.decision_note}
                    </div>
                  )}
                  {data?.can_approve_dates && r.status === "Pending" && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await decideDate.trigger(
                              { id: r.id, decision: "Approved", note: null },
                              { skipCache: true },
                            );
                            toast.success("Approved, and the date has moved.");
                            load();
                          } catch (err) {
                            toast.error(writeErrorMessage(err));
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => setNoteFor(`date:${r.id}`)}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                  {noteFor === `date:${r.id}` && (
                    <div className="mt-2 space-y-2">
                      <Textarea
                        rows={3}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Say why. Sending it back without a reason moves the guessing to the other person."
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            try {
                              await decideDate.trigger(
                                {
                                  id: r.id,
                                  decision: "Declined",
                                  note: noteText,
                                },
                                { skipCache: true },
                              );
                              setNoteFor(null);
                              setNoteText("");
                              toast.success(
                                "Declined, and they have been told.",
                              );
                              load();
                            } catch (err) {
                              toast.error(writeErrorMessage(err));
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          Decline it
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setNoteFor(null);
                            setNoteText("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------------------------------------------------------------- */}
        <TabsContent value="policy" className="min-h-0 flex-1 overflow-auto">
          {!data?.policy ? (
            <p className="p-6 text-sm text-muted-foreground">
              No policy is in force. Apply db/migrations/0121.
            </p>
          ) : (
            <div className="space-y-4 py-2">
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {ruleSentences(data.policy).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
              <div className="rounded-lg border border-border p-3 text-sm">
                <div className="font-medium">Settlement</div>
                <p className="mt-1 text-muted-foreground">
                  {data.policy.payroll_release_enabled
                    ? `Switched on, against ${data.policy.legal_signoff_ref ?? "no recorded advice"}.`
                    : "Switched off. No charge on this tab can reach a payslip or an invoice."}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Changing the policy closes this row and opens a new one, so
                  what somebody was charged under the old amount is never
                  restated. A reason is required.
                </p>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <RaiseSinDialog
        open={raiseOpen}
        onOpenChange={setRaiseOpen}
        people={people}
        amountZar={data?.policy?.amount_zar ?? null}
        onRaised={load}
      />
    </div>
  );
}
